<script setup>
import { computed, watch, ref, inject, onMounted } from 'vue'
import { useVModel } from '#/general/useVModel'
import { useAppMenu } from '#/general/useAppMenu'
const { appMenuCounter } = useAppMenu()
const props = defineProps({
  visible: Boolean,
  position: String,
  attachToParent: Boolean,
  persistent: Boolean,
  tooltipMode: Boolean,
  contentWidth: { type: String, default: '100%' },
  offset: Array,
  centerParent: { type: Boolean, default: false }
})
const emit = defineEmits(['update:visible'])
const { application, isBrowser } = inject('Shared')
let visible
if (props.tooltipMode) {
  visible = ref(false)
} else {
  visible = useVModel(props, 'visible')
}
const menuOpening = ref(false)
const currentCounter = ref(Number(appMenuCounter.value))
watch(visible, v => {
  if (v) {
    currentCounter.value = appMenuCounter.value + 1
    menuOpening.value = true
    setTimeout(() => {
      appMenuCounter.value += 1
      menuOpening.value = false
    }, 200)
  }
  emit('update:visible', v)
}, { immediate: true })

watch(appMenuCounter, () => {
  if (appMenuCounter.value !== currentCounter.value) {
    visible.value = false
  }
})

const contentClasses = computed(() => ({
  'top-position': props.position === 'top',
  'app-menu-bottom': props.position === 'bottom',
  'fit-position': props.position === 'fit'
}))

const transform = computed(() => {
  switch (props.position) {
    case 'top': return 'translateY(-100%)'
    case 'bottom': return 'translateY(100%)'
  }
  return ''
})

const horizontalOffset = computed(() => {
  if (!props.offset || props.offset.length < 2 || [null, undefined].includes(props.offset[0])) {
    if (props.position === 'left') {
      return '-5px'
    } else if (props.position === 'right') {
      return '5px'
    }
    return '0px'
  }
  return props.offset[0] + 'px'
})
const verticalOffset = computed(() => {
  if (!props.offset || props.offset.length < 2 || [null, undefined].includes(props.offset[1])) {
    if (props.position === 'top') {
      return '-5px'
    } else if (props.position === 'bottom') {
      return '-5px'
    }
    return '0px'
  }
  return props.offset[1] + 'px'
})

const zIndex = computed(() => {
  return 1100 + appMenuCounter.value
})
const parentId = `parentId${zIndex.value}`
const childrenId = `childrenId${zIndex.value}`

onMounted(() => {
  document.addEventListener('click', (event) => {
    let container
    if (application === 'WIDGET') {
      if (process.env.NODE_ENV === 'production') {
        const shadow = document.querySelector('#app').shadowRoot
        container = shadow.querySelector(`#${parentId}`)
      } else {
        container = document.getElementById(parentId)
      }
    } else {
      container = document.getElementById(parentId)
    }
    if (container) {
      if (!container.contains(event.target) && !menuOpening.value) {
        visible.value = false
      }
    }
  })
})

const mouseLeave = () => {
  if (!props.persistent) {
    visible.value = false
  }
}

function parentHovered () {
  if (props.tooltipMode) {
    visible.value = true
  }
}
</script>

<template lang="pug">
.appMenuContainer(:style="attachToParent ? 'position: relative' : ''" @mouseleave="mouseLeave()")
  div(:id="parentId" @mouseover="parentHovered" :class="centerParent ? 'centerParent' : ''")
    slot(name="parent")
  transition(name="expand")
    .menu--content(:id="childrenId" v-show="visible" :class="contentClasses" v-if="isBrowser")
      slot(name="content")
</template>

<style lang="sass">
.appMenuContainer
  .centerParent
    display: flex
    justify-content: center
    align-items: center
  .top-position
    top: v-bind(verticalOffset)
  .app-menu-bottom
    bottom: v-bind(verticalOffset)
  .fit-position
    top: v-bind(verticalOffset)
    left: v-bind(horizontalOffset)
  .menu--content
    position: absolute
    left: v-bind(horizontalOffset)
    width: v-bind(contentWidth)
    z-index: v-bind(zIndex)
    transform: v-bind(transform)
  .expand-enter-active, .expand-leave-active
    transition: max-height .2s ease
    max-height: 200px
  .expand-enter, .expand-leave-to
    max-height: 0
    overflow: hidden
</style>
